import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { IconChevron, IconClock, IconGrip, IconSearch, IconX } from '@/components/ui/Icon';
import { useDragReorder } from '@/shared/hooks/useDragReorder';
import { useMediaQuery, NARROW_QUERY } from '@/shared/hooks/useMediaQuery';
import { useMinuteTick } from '@/shared/hooks/useMinuteTick';
import { registerWidget, type WidgetRenderProps, type WidgetConfigProps } from '../registry';
import { citiesOf, clockRows, hasZone, localTimeZone, searchZones, utcLabel, utcOffsetMinutes, type ClockCity } from './worldClock';

// World clock tile: the time in the user's cities, re-read on each minute
// boundary. A row: the city, its time, the day there ("Tomorrow"), and how far
// ahead or behind the user's own time it is ("+7h"); the zone and its UTC
// offset in the tooltip. Cities are IANA zones from the browser's list
// (worldClock.ts), saved in the tile's config as { cities: [{ zone, label }] };
// until the user edits them: their own zone, New York and London.

// One column per 4 grid columns, so Small, Medium and Full width read 1, 2 and
// 3 across; one on a phone, where every tile spans the screen.
function columnsFor(w: number | undefined, narrow: boolean): number {
  return narrow ? 1 : Math.max(1, Math.min(3, Math.floor((w ?? 4) / 4)));
}

function WorldClockRender({ config, w }: WidgetRenderProps) {
  const now = useMinuteTick();
  const narrow = useMediaQuery(NARROW_QUERY);
  const local = localTimeZone();
  const cities = citiesOf(config, local);
  if (cities.length === 0) {
    return <div className="text-sm text-muted">No cities yet. Customize, then Configure this tile to add some.</div>;
  }
  return (
    <ul aria-label="World clock" className="grid gap-x-4"
      style={{ gridTemplateColumns: `repeat(${columnsFor(w, narrow)}, minmax(0, 1fr))` }}>
      {clockRows(cities, now, local).map((r) => (
        <li key={`${r.zone}|${r.label}`} title={`${r.label} · ${r.zone} · ${r.utc}`}
          className="flex items-center gap-3 min-w-0 py-1.5 border-b border-border-light">
          <div className="flex-1 min-w-0">
            <div className="text-body font-medium text-primary-text truncate">{r.label}</div>
            <div className="text-meta text-muted truncate">
              <span aria-hidden="true">{r.day} · {r.diff}</span>
              <span className="sr-only">{r.day}, {r.diffSpoken}, {r.utc}</span>
            </div>
          </div>
          {/* Fixed width and tabular digits: 9:59 turning 10:00 moves nothing. */}
          <time dateTime={r.iso} className="shrink-0 min-w-[4.75rem] text-right font-numeric text-primary-text whitespace-nowrap">
            <span className="text-[1.25rem] font-semibold leading-none">{r.hm}</span>
            <span className="ml-0.5 text-meta font-medium text-muted">{r.ampm}</span>
          </time>
        </li>
      ))}
    </ul>
  );
}

function RowButton({ act, label, disabled, onClick, children }: {
  act: 'up' | 'down' | 'remove'; label: string; disabled?: boolean; onClick: () => void; children: ReactNode;
}) {
  return (
    <button type="button" data-act={act} aria-label={label} title={label} disabled={disabled} onClick={onClick}
      className="shrink-0 inline-flex items-center justify-center w-[22px] h-[22px] rounded-md text-muted hover:text-primary-text hover:bg-row-highlight disabled:opacity-40 disabled:pointer-events-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary">
      {children}
    </button>
  );
}

// Configure: the cities in order (drag, or the arrow buttons from the
// keyboard), each removable, and a search that adds one (↑/↓ pick, Enter adds,
// Esc clears). Changes apply to the tile at once; Save keeps them.
function WorldClockConfig({ config, onChange }: WidgetConfigProps) {
  const cities = citiesOf(config, localTimeZone());
  const save = (next: ClockCity[]) => onChange({ ...(config || {}), cities: next });
  const { handleProps, rowProps } = useDragReorder(cities, save);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [said, setSaid] = useState('');
  const hits = useMemo(() => searchZones(query), [query]);
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Where keyboard focus lands once a move or remove has re-rendered the list:
  // the moved city's arrow, the next city's remove, else the search box.
  const focusNext = useRef<string | null>(null);
  useEffect(() => {
    const sel = focusNext.current;
    if (!sel) return;
    focusNext.current = null;
    const el = rootRef.current?.querySelector<HTMLButtonElement>(sel);
    (el && !el.disabled ? el : inputRef.current)?.focus();
  });

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= cities.length) return;
    const next = cities.slice();
    [next[i], next[j]] = [next[j], next[i]];
    // Stay on the arrow that was pressed; at the top or bottom, on the other one.
    const atEnd = dir < 0 ? j === 0 : j === next.length - 1;
    const act = atEnd ? (dir < 0 ? 'down' : 'up') : (dir < 0 ? 'up' : 'down');
    focusNext.current = `[data-row="${j}"] [data-act="${act}"]`;
    setSaid(`${next[j].label} moved to ${j + 1} of ${next.length}`);
    save(next);
  };
  const remove = (i: number) => {
    const next = cities.filter((_, k) => k !== i);
    focusNext.current = next.length ? `[data-row="${Math.min(i, next.length - 1)}"] [data-act="remove"]` : 'input';
    setSaid(`${cities[i].label} removed`);
    save(next);
  };
  const add = (c: ClockCity) => {
    if (hasZone(cities, c.zone)) { setSaid(`${c.label} is already on the clock`); return; }
    save([...cities, c]);
    setSaid(`${c.label} added`);
    setQuery('');
    setActive(0);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (hits[active]) add(hits[active]); }
    else if (e.key === 'Escape' && query) { e.preventDefault(); e.stopPropagation(); setQuery(''); setActive(0); }
  };
  const now = new Date();
  const expanded = hits.length > 0;

  return (
    <div ref={rootRef} className="grid gap-2">
      <span className="text-[0.75rem] text-muted">Cities (drag or use the arrows to reorder)</span>
      {cities.length === 0
        ? <div className="text-[0.75rem] text-muted">No cities yet. Search below to add one.</div>
        : (
          <ul aria-label="Cities on the clock" className="max-h-56 overflow-auto rounded-md border border-border-light">
            {cities.map((c, i) => {
              const rp = rowProps(i);
              return (
                <li key={`${c.zone}|${c.label}`} data-row={i} onDragOver={rp.onDragOver} onDrop={rp.onDrop}
                  className={`flex items-center gap-1.5 px-2 py-1 text-[0.8125rem] ${rp.isOver ? 'ring-2 ring-primary' : ''}`}>
                  <span {...handleProps(i)} aria-hidden="true" className="text-muted cursor-grab select-none" title="Drag to reorder"><IconGrip size={13} /></span>
                  <span className="flex-1 min-w-0 truncate" title={c.zone}>{c.label} <span className="text-muted">({c.zone})</span></span>
                  <RowButton act="up" label={`Move ${c.label} up`} disabled={i === 0} onClick={() => move(i, -1)}><IconChevron size={13} className="rotate-180" /></RowButton>
                  <RowButton act="down" label={`Move ${c.label} down`} disabled={i === cities.length - 1} onClick={() => move(i, 1)}><IconChevron size={13} /></RowButton>
                  <RowButton act="remove" label={`Remove ${c.label}`} onClick={() => remove(i)}><IconX size={13} /></RowButton>
                </li>
              );
            })}
          </ul>
        )}
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none"><IconSearch size={13} /></span>
        <input ref={inputRef} value={query} onChange={(e) => { setQuery(e.target.value); setActive(0); }} onKeyDown={onKey}
          role="combobox" aria-label="Add a city" aria-autocomplete="list" aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined} aria-activedescendant={expanded ? `${listId}-${active}` : undefined}
          placeholder="Add a city or time zone…"
          className="w-full h-[28px] pl-7 pr-2 rounded-md border border-border bg-surface text-[0.8125rem]" />
      </div>
      {expanded && (
        <ul id={listId} role="listbox" aria-label="Matching cities" className="max-h-44 overflow-auto rounded-md border border-border-light">
          {hits.map((h, i) => {
            const added = hasZone(cities, h.zone);
            return (
              <li key={`${h.zone}|${h.label}`} id={`${listId}-${i}`} role="option" aria-selected={i === active} aria-disabled={added || undefined}
                onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => add(h)}
                className={`flex items-center gap-2 px-2 py-1 text-[0.8125rem] cursor-pointer ${i === active ? 'bg-row-highlight' : ''} ${added ? 'text-muted' : 'text-primary-text'}`}>
                <span className="flex-1 min-w-0 truncate">{h.label} <span className="text-muted">({h.zone})</span></span>
                <span className="shrink-0 text-meta text-muted font-numeric">{added ? 'Added' : utcLabel(utcOffsetMinutes(now, h.zone))}</span>
              </li>
            );
          })}
        </ul>
      )}
      {query.trim() && !expanded && <div className="text-[0.75rem] text-muted">No city or time zone matches.</div>}
      <div aria-live="polite" className="sr-only">{said}</div>
    </div>
  );
}

registerWidget({
  type: 'world_clock', label: 'World clock', defaultSize: 'S',
  icon: <IconClock size={13} />,
  accent: '#0284c7',
  ConfigForm: WorldClockConfig, Render: WorldClockRender,
});
